/**
 * HELM — Yevhen's morning card (A7Y-3870, phase 1B read-only build; A7Y-3872
 * phase 3 adds settings, proposals, scorecard, call intake, past days and the
 * phone layout).
 *
 * `/admin/cmo/helm`, gated by `AdminLayout`'s existing superadmin check (TPF,
 * 2026-09-10: no per-user owner check). Reads `helm_runs` / `helm_actions` /
 * `helm_config` only through `/admin/helm/*` on a7y-api — this page never
 * touches Firestore directly (D2).
 *
 * The last-good snapshot (`overview.current`) stays visible at all times; a
 * Refresh click or an in-flight run only ever adds a banner alongside it,
 * never replaces it with a loading state (ticket body: "the page keeps the
 * last-good snapshot visible with its age and a 'refreshing, started HH:MM'
 * line until the new snapshot lands").
 *
 * Past-day mode (`?run=<runId>`, D14): the page instead fetches that one run
 * through `getHelmRun`, overlaid with actions as they stood at its
 * `completedAt`, and renders it inside `HelmReadOnlyContext` so every
 * per-item action surface removes itself (D15) — Refresh, the undo banner,
 * `SourceConfigEditor` and `CaptureBox` are hidden outright here, since none
 * of them make sense against a historical snapshot.
 *
 * Every mutation below (`refreshHelm`, `createHelmAction`
 * via `act`, `putHelmConfig`, `undoHelmAction`) is wrapped so a rejection
 * surfaces as `pageError` instead of an unhandled promise rejection —
 * `useHelmActions` already rolls its own optimistic patch back before
 * rethrowing, so this page only owns display. On a rejected action the
 * overview is also invalidated (not just rolled back locally), so a stale
 * proposal approval's 409 (D8) shows the proposal's revised state on refetch.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import * as Tabs from '@radix-ui/react-tabs';
import { HELM_PAST_DAYS_MAX, helmNextRunDay } from '@a7y/platform-health/helm';
import type {
  HelmActionInput,
  HelmOverviewResponse,
  HelmRunView,
  HelmSourceConfig,
} from '@a7y/platform-health/helm';
import { Badge, Button } from '@a7y/tailwind-catalyst';
import { PageHeader } from '@/components/PageHeader';
import { HelmViewNav } from '../HelmViews';
import { api } from '@/lib/api';
import { fmtLocal, relativeAgo, useNow } from '@/pages/autobuild/shared';
import { HELM_OVERVIEW_QUERY_KEY, useHelmActions } from './useHelmActions';
import { HELM_REFRESH_TIMEOUT_MS, isHelmRefreshServed } from './refresh';
import { HelmReadOnlyContext } from './readOnly';
import { HelmRecordContext, buildRecordIndex, writeRecordAction } from './recordActions';
import type { CloseDetails, HelmRecordActions, RecordTarget } from './recordActions';
import { CRM_QUERY_ROOT } from '../crm/useCrmWrite';
import { isoDate } from '../crm/accountView';
import { splitAccountName } from '../crm/links';
import { TodayCard } from './sections/TodayCard';
import type { HelmLiveRecord } from './sections/TodayCard';
import { ShippedSection } from './sections/ShippedSection';
import { JudgmentQueue } from './sections/JudgmentQueue';
import { CallIntake } from './sections/CallIntake';
import { Scorecard } from './sections/Scorecard';
import { Proposals } from './sections/Proposals';
import { Changes } from './sections/Changes';
import { Watched } from './sections/Watched';
import { OverdueStale } from './sections/OverdueStale';
import { isStaleCloseProposal, visibleCommitmentKeys } from './staleRows';
import { RunHistory } from './sections/RunHistory';
import { DayPicker, PastDayBanner } from './sections/PastDay';
import { SourceHealth } from './sections/SourceHealth';
import { SourceConfigEditor } from './sections/SourceConfigEditor';
import { CaptureBox } from './sections/CaptureBox';
import { HelmSection } from './sections/HelmSection';

const RUNS_LIMIT = 200;
const POLL_INTERVAL_MS = 15_000;

/**
 * The five tabs the page's IA is grouped into (A7Y-4407 round 2): the tab
 * bar itself sits directly under the header and its banners so it is always
 * above the fold — `TodayCard` moved off "always visible above the tabs"
 * and into the `today` tab, as the default landing tab, so the "nothing
 * critical slips past Yevhen" guarantee is unchanged (default tab, not
 * scroll position, is what guarantees it's seen). `settings` is omitted
 * from the bar entirely in past mode — a historical run has no source
 * config of its own to edit — so `resolveHelmTab` below refuses to land on
 * it there even from a stale `?tab=settings` URL.
 */
type HelmTab = 'today' | 'inbox' | 'trends' | 'runs' | 'settings';
const HELM_TABS: { value: HelmTab; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'inbox', label: 'Inbox' },
  { value: 'trends', label: 'Trends' },
  { value: 'runs', label: 'Runs' },
  { value: 'settings', label: 'Settings' },
];

function resolveHelmTab(rawTab: string | null, isPast: boolean): HelmTab {
  if (rawTab === 'today' || rawTab === 'inbox' || rawTab === 'trends' || rawTab === 'runs') {
    return rawTab;
  }
  if (rawTab === 'settings' && !isPast) return 'settings';
  return 'today';
}

// Radix Tabs (A7Y-4407 round 3), mirroring the finance P&L page's own
// Tabs.Root/List/Trigger usage (`pages/finance/PnlPage.tsx`) rather than a
// hand-rolled tab bar. `data-[state=active]` is Radix's own attribute for
// the selected trigger.
const TAB_TRIGGER_CLASSES =
  'flex min-h-11 shrink-0 items-center gap-1.5 border-b-2 border-transparent px-4 text-sm font-medium text-zinc-500 hover:text-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:text-zinc-400 dark:hover:text-zinc-300 dark:data-[state=active]:text-blue-400';

/** `Mon 19 Oct` for a `YYYY-MM-DD` day — the next run's day on the snapshot stamp (A7Y-5492). */
function fmtRunDay(day: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(`${day}T12:00:00Z`));
}

function describeError(prefix: string, err: unknown): string {
  const detail = err instanceof Error ? err.message : String(err);
  return `${prefix}: ${detail}`;
}

export function HelmPage() {
  const queryClient = useQueryClient();
  const nowMs = useNow();
  const [searchParams, setSearchParams] = useSearchParams();
  const runId = searchParams.get('run');
  const isPast = runId != null;
  const activeTab = resolveHelmTab(searchParams.get('tab'), isPast);
  const visibleTabs = HELM_TABS.filter((tab) => tab.value !== 'settings' || !isPast);

  // Set the moment a Refresh click is accepted; cleared once a run that
  // supersedes it has been observed and resolved (see `refresh.ts`).
  const [requestedAt, setRequestedAt] = useState<string | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [submittingCapture, setSubmittingCapture] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const overviewQuery = useQuery({
    queryKey: HELM_OVERVIEW_QUERY_KEY,
    queryFn: api.getHelmOverview,
  });
  const overview = overviewQuery.data;
  const shouldPoll = Boolean(overview?.inFlight) || requestedAt != null;

  // Polls only while a run is in flight (server-reported, any session) or
  // this page just requested one. Clearing `requestedAt` happens inside the
  // async tick, once a run that actually supersedes the click has resolved
  // (`isHelmRefreshServed`) — never merely because `inFlight` is null, since
  // the run doc doesn't exist yet for the first several minutes after
  // dispatch (checkout + npm ci + CLI install run first).
  useEffect(() => {
    if (!shouldPoll) return;
    let cancelled = false;
    const id = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: HELM_OVERVIEW_QUERY_KEY }).then(() => {
        if (cancelled) return;
        const latest = queryClient.getQueryData<HelmOverviewResponse>(HELM_OVERVIEW_QUERY_KEY);
        if (latest && requestedAt && isHelmRefreshServed(latest, requestedAt)) {
          setRequestedAt(null);
        }
      });
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [shouldPoll, queryClient, requestedAt]);

  // Bounded safety valve: a genuinely lost run must not show "Refreshing"
  // forever.
  useEffect(() => {
    if (!requestedAt) return;
    const id = window.setTimeout(() => setRequestedAt(null), HELM_REFRESH_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [requestedAt]);

  // Widened to `HELM_PAST_DAYS_MAX` (D14) so the run history and day picker
  // below can reach "any past run", not just the last three weeks
  // (`HELM_SWEEP_RUN_LOOKBACK_DAYS`).
  const runsQuery = useQuery({
    queryKey: ['helm-runs', RUNS_LIMIT, HELM_PAST_DAYS_MAX],
    queryFn: () => api.getHelmRuns(RUNS_LIMIT, HELM_PAST_DAYS_MAX),
  });

  const runDetailQuery = useQuery({
    queryKey: ['helm-run', runId],
    queryFn: () => api.getHelmRun(runId as string),
    enabled: isPast,
  });

  const configQuery = useQuery({
    queryKey: ['helm-config'],
    queryFn: api.getHelmConfig,
    enabled: !isPast,
  });
  // A7Y-5210: the capture box's account picker. Off production the record answers
  // CRM_UNAVAILABLE and the picker is simply not offered.
  const accountsQuery = useQuery({
    queryKey: [...CRM_QUERY_ROOT, 'accounts'],
    queryFn: api.getCrmAccounts,
    enabled: !isPast,
    retry: false,
  });
  // A7Y-5492: the record as it is now — the open roster items, read with This week's
  // own query (same key, same endpoint), so Critical and Due today drop a row the
  // moment it is closed or re-dated, and Due today counts what This week's Today does.
  // Off production, or unreadable, the card shows the run's rows.
  const openRosterQuery = useQuery({
    queryKey: [...CRM_QUERY_ROOT, 'roster-items', 'open'],
    queryFn: () => api.getCrmRosterItems('open'),
    enabled: !isPast,
    retry: false,
  });

  // A7Y-5873: a resolved change from a run that stored no name reads it from the
  // previous run, where the row still stood. Fetched only when one needs it.
  const changesSource = isPast ? runDetailQuery.data?.run : overviewQuery.data?.current;
  const previousRunId = changesSource?.facts.changes_since_last.previousRunId ?? null;
  const resolvedNeedsName =
    changesSource?.facts.changes_since_last.resolved.some((c) => !c.name) ?? false;
  const previousRunQuery = useQuery({
    queryKey: ['helm-run', previousRunId],
    queryFn: () => api.getHelmRun(previousRunId as string),
    enabled: previousRunId !== null && resolvedNeedsName && activeTab === 'trends',
    retry: false,
  });

  const { act, undo, undoActionById, canUndo } = useHelmActions();

  // Defined before the early returns below (Karen round 1 F10) so a past run
  // stuck loading, or refused as not-viewable (404/409), still offers a way
  // back to today rather than a dead end.
  const handleBackToToday = () => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('run');
        return next;
      },
      { replace: false }
    );
  };

  const backToTodayButton = (
    <Button outline data-testid="helm-back-to-today" onClick={handleBackToToday}>
      Back to today
    </Button>
  );

  if (isPast) {
    if (runDetailQuery.isLoading) {
      return (
        <div className="space-y-3 p-6">
          <p className="text-sm text-zinc-500">Loading…</p>
          {backToTodayButton}
        </div>
      );
    }
    if (runDetailQuery.isError || !runDetailQuery.data) {
      return (
        <div className="space-y-3 p-6">
          <p className="text-sm text-red-600 dark:text-red-400">This run is not viewable.</p>
          {backToTodayButton}
        </div>
      );
    }
  } else {
    if (overviewQuery.isLoading) {
      return <div className="p-6 text-sm text-zinc-500">Loading…</div>;
    }
    if (overviewQuery.isError || !overview) {
      return <div className="p-6 text-sm text-red-600 dark:text-red-400">Failed to load HELM.</div>;
    }
  }

  const handleRefresh = async () => {
    try {
      const res = await api.refreshHelm();
      setRequestedAt(res.requestedAt);
      setPageError(null);
      await queryClient.invalidateQueries({ queryKey: HELM_OVERVIEW_QUERY_KEY });
      // A7Y-5492: Refresh re-reads the record too, so Critical and Due today are current at once.
      await queryClient.invalidateQueries({
        queryKey: [...CRM_QUERY_ROOT, 'roster-items', 'open'],
      });
    } catch (err) {
      setPageError(describeError('Refresh failed', err));
    }
  };

  const handleAction = async (input: HelmActionInput) => {
    try {
      await act(input);
      setPageError(null);
    } catch (err) {
      setPageError(describeError('Action failed', err));
      // A stale proposal approval (D8) rejects with 409; the optimistic
      // patch is already rolled back inside `act`, but a fresh fetch is what
      // actually shows the proposal's revised state (e.g. now `stale`).
      await queryClient.invalidateQueries({ queryKey: HELM_OVERVIEW_QUERY_KEY });
    }
  };

  const handleUndo = async () => {
    try {
      await undo();
      setPageError(null);
    } catch (err) {
      setPageError(describeError('Undo failed', err));
    }
  };

  const handleUndoActionById = async (actionId: string) => {
    try {
      await undoActionById(actionId);
      setPageError(null);
    } catch (err) {
      setPageError(describeError('Undo failed', err));
    }
  };

  const handleSaveConfig = async (config: HelmSourceConfig) => {
    setSavingConfig(true);
    try {
      await api.putHelmConfig(config);
      setPageError(null);
      await queryClient.invalidateQueries({ queryKey: ['helm-config'] });
    } catch (err) {
      setPageError(describeError('Save failed', err));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleCapture = async (note: string) => {
    setSubmittingCapture(true);
    try {
      await act({ kind: 'capture', note });
      setPageError(null);
    } catch (err) {
      setPageError(describeError('Capture failed', err));
    } finally {
      setSubmittingCapture(false);
    }
  };

  // A7Y-5210: a capture filed under an account is a history line on it, not a HELM capture.
  const handleCaptureToAccount = async (accountId: string, note: string): Promise<boolean> => {
    setSubmittingCapture(true);
    try {
      await api.appendCrmEvent({
        accountId,
        date: isoDate(new Date()),
        source: 'note',
        text: note,
      });
      await queryClient.invalidateQueries({ queryKey: CRM_QUERY_ROOT });
      setPageError(null);
      return true;
    } catch (err) {
      setPageError(describeError('Capture failed', err));
      return false;
    } finally {
      setSubmittingCapture(false);
    }
  };

  // A7Y-5210: Done, Defer and Waiting on for a roster row write the record first; the
  // overlay then hides the row on today's card, with no Undo (it could not undo the record).
  const handleRecordAction = async (
    target: RecordTarget,
    input: HelmActionInput,
    close: CloseDetails | null
  ) => {
    try {
      await writeRecordAction(target, input, close, isoDate(new Date()));
    } catch (err) {
      setPageError(describeError('Could not save to the record', err));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: CRM_QUERY_ROOT });
    try {
      await act(close ? { ...input, note: close.outcome } : input, { undoable: false });
      setPageError(null);
    } catch (err) {
      setPageError(describeError('Saved to the record; hiding the row failed', err));
    }
  };

  const handleOpenRun = (id: string) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('run', id);
        return next;
      },
      { replace: false }
    );
  };

  // Copy-and-mutate, same as `handleBackToToday`/`handleOpenRun` above, so a
  // tab click preserves `?run=` rather than clobbering it.
  const handleTabChange = (tab: HelmTab) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('tab', tab);
        return next;
      },
      { replace: false }
    );
  };

  const runDetail = runDetailQuery.data;
  const view: HelmRunView | null = isPast ? (runDetail?.run ?? null) : (overview?.current ?? null);
  const recordActions: HelmRecordActions | null =
    !isPast && view
      ? {
          targets: buildRecordIndex(view.facts),
          onRecordAction: (target, input, close) => void handleRecordAction(target, input, close),
        }
      : null;
  const captureAccounts =
    accountsQuery.data?.imported === true
      ? accountsQuery.data.accounts.map((item) => ({
          id: item.account.id,
          // A7Y-5492: the picker shows the plain name, never the record's markdown.
          name: splitAccountName(item.account.name).name,
        }))
      : null;
  const liveRecord: HelmLiveRecord | null =
    !isPast && openRosterQuery.data?.imported === true
      ? { items: openRosterQuery.data.items, today: isoDate(new Date(nowMs)) }
      : null;
  const suppressedKeys = new Set(
    isPast ? (runDetail?.suppressedKeys ?? []) : (overview?.suppressedKeys ?? [])
  );
  const latestAttempt = overview?.latestAttempt ?? null;
  const latestAttemptFailed =
    latestAttempt != null &&
    (latestAttempt.status === 'failed' || latestAttempt.status === 'abandoned');
  const inFlightStartedAt = overview?.inFlight?.startedAt ?? requestedAt;
  const runs = runsQuery.data?.runs ?? [];

  // Tab-label badges (A7Y-4407): every count is the same filter its own
  // section already applies, read off `view.facts` — no new fields.
  // The Stale rows the Inbox tab renders; their open close proposals are
  // decided on those rows, so Proposals leaves them out (and so does the count).
  const staleKeys = new Set(
    view ? visibleCommitmentKeys(view.facts.stale, view.facts.commitments, suppressedKeys) : []
  );
  const inboxCount = view
    ? view.facts.proposals.filter(
        (p) => p.status === 'open' && p.decision == null && !isStaleCloseProposal(p, staleKeys)
      ).length +
      view.facts.deployed.filter((item) => !suppressedKeys.has(item.key)).length +
      view.facts.judgment_queue.items.filter((item) => !suppressedKeys.has(item.key)).length +
      view.facts.drive_notes.filter(
        (note) => note.accounts.length === 0 && !suppressedKeys.has(note.key)
      ).length +
      visibleCommitmentKeys(view.facts.overdue, view.facts.commitments, suppressedKeys).length +
      staleKeys.size
    : 0;
  const trendsCount = view
    ? view.facts.changes_since_last.added.filter((c) => !suppressedKeys.has(c.key)).length
    : 0;
  const runsNonFresh =
    !isPast && overview
      ? Object.values(overview.sourceHealth).some((health) => health.state !== 'fresh')
      : false;

  return (
    <HelmReadOnlyContext.Provider value={isPast}>
      <HelmRecordContext.Provider value={recordActions}>
        {/* A7Y-5210: HELM's views; outside the column below so the tab bar stays the header's next sibling. */}
        <HelmViewNav />
        <div className="space-y-6">
          <PageHeader
            title="HELM"
            subtitle="Daily marketing brief — critical items, inbox, meetings, Slack and pipeline decisions."
          >
            {!isPast && (
              <div className="flex flex-wrap items-center gap-3">
                {view && (
                  // A7Y-5492: the run is a morning snapshot; the stamp says so, and when the next one is.
                  <span data-testid="helm-snapshot-age" className="text-xs text-zinc-500">
                    As of {fmtLocal(view.summary.window?.to ?? view.summary.startedAt)} (
                    {relativeAgo(view.summary.window?.to ?? view.summary.startedAt, nowMs)}) · next
                    run {fmtRunDay(helmNextRunDay(view.facts.localDate))}
                    {liveRecord ? ' · Critical and Due today read the record now' : ''}
                  </span>
                )}
                {inFlightStartedAt && (
                  <span
                    data-testid="helm-refreshing"
                    className="text-xs text-blue-600 dark:text-blue-400"
                  >
                    Refreshing, started {fmtLocal(inFlightStartedAt).split(' ')[1] ?? ''}
                  </span>
                )}
                <Button data-testid="helm-refresh-button" onClick={handleRefresh}>
                  Refresh
                </Button>
              </div>
            )}
          </PageHeader>

          {pageError && (
            <div
              data-testid="helm-page-error"
              className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
            >
              {pageError}
            </div>
          )}

          {isPast && runDetail && (
            <PastDayBanner summary={runDetail.run.summary} onBack={handleBackToToday} />
          )}

          {/* Today's own latest-attempt failure is irrelevant to a past run's
            reconstructed snapshot (Karen round 1 F13) — this describes
            today's most recent run, not the one being viewed. */}
          {!isPast && latestAttemptFailed && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
              The latest run {latestAttempt?.status === 'abandoned' ? 'was abandoned' : 'failed'}
              {latestAttempt?.failedStage ? ` at ${latestAttempt.failedStage}` : ''}. The card below
              is the last-good snapshot.
            </div>
          )}

          {!isPast && canUndo && (
            <div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 p-2 text-sm dark:border-zinc-700 dark:bg-zinc-900">
              <span>Action applied.</span>
              <Button plain data-testid="helm-undo" onClick={handleUndo}>
                Undo
              </Button>
            </div>
          )}

          {/* Directly under the header and its banners, so it is always above
            the fold (A7Y-4407 round 2) — nothing above this point depends on
            which run loaded, so the tab bar never waits on `view`. Radix
            Tabs (round 3), same as the finance P&amp;L page. */}
          <Tabs.Root value={activeTab} onValueChange={(value) => handleTabChange(value as HelmTab)}>
            <Tabs.List
              data-testid="helm-tab-bar"
              className="flex overflow-x-auto whitespace-nowrap border-b border-zinc-200 dark:border-zinc-700"
            >
              {visibleTabs.map((tab) => (
                <Tabs.Trigger
                  key={tab.value}
                  value={tab.value}
                  data-testid={`helm-tab-${tab.value}`}
                  className={TAB_TRIGGER_CLASSES}
                >
                  {tab.label}
                  {tab.value === 'inbox' && (
                    <Badge data-testid="helm-tab-inbox-count" color="zinc">
                      {inboxCount}
                    </Badge>
                  )}
                  {tab.value === 'trends' && trendsCount > 0 && (
                    <Badge data-testid="helm-tab-trends-count" color="red">
                      {trendsCount}
                    </Badge>
                  )}
                  {tab.value === 'runs' && runsNonFresh && (
                    <span
                      aria-hidden="true"
                      data-testid="helm-tab-runs-dot"
                      className="h-2 w-2 shrink-0 rounded-full bg-amber-500"
                    />
                  )}
                </Tabs.Trigger>
              ))}
            </Tabs.List>

            <Tabs.Content value="today" className="pt-4 sm:pt-6">
              {view ? (
                // The comparison view below re-renders this same primary-style
                // card alongside the other style, so this primary copy carries
                // its own test id — otherwise a query for card-line content
                // cannot tell "today's card" from its own comparison copy.
                <div data-testid="helm-today-card">
                  <TodayCard
                    card={view.cards[view.cards.primary]}
                    facts={view.facts}
                    suppressedKeys={suppressedKeys}
                    onAction={handleAction}
                    record={liveRecord}
                  />
                </div>
              ) : (
                <p className="text-sm text-zinc-500">No successful HELM run yet.</p>
              )}
            </Tabs.Content>

            <Tabs.Content value="inbox" className="pt-4 sm:pt-6">
              <div className="space-y-6">
                {view && (
                  <>
                    <Proposals
                      proposals={view.facts.proposals}
                      facts={view.facts}
                      writebacks={overview?.writebacks ?? []}
                      staleKeys={staleKeys}
                      onAction={handleAction}
                      onUndo={handleUndoActionById}
                    />
                    <ShippedSection
                      items={view.facts.deployed}
                      suppressedKeys={suppressedKeys}
                      onAction={handleAction}
                    />
                    <JudgmentQueue
                      queue={view.facts.judgment_queue}
                      suppressedKeys={suppressedKeys}
                      onAction={handleAction}
                    />
                    <CallIntake
                      notes={view.facts.drive_notes}
                      suppressedKeys={suppressedKeys}
                      onAction={handleAction}
                    />
                    <Watched commitments={view.facts.commitments} onAction={handleAction} />
                    <OverdueStale
                      commitments={view.facts.commitments}
                      overdue={view.facts.overdue}
                      stale={view.facts.stale}
                      proposals={view.facts.proposals}
                      suppressedKeys={suppressedKeys}
                      onAction={handleAction}
                    />
                  </>
                )}
                {!isPast && (
                  <CaptureBox
                    captures={view?.facts.captures ?? []}
                    submitting={submittingCapture}
                    onSubmit={handleCapture}
                    onUndo={handleUndoActionById}
                    accounts={captureAccounts}
                    onSubmitToAccount={handleCaptureToAccount}
                  />
                )}
              </div>
            </Tabs.Content>

            <Tabs.Content value="trends" className="pt-4 sm:pt-6">
              {view && (
                <div className="space-y-6">
                  {/* A7Y-5873: today's freshness is the source health Runs shows. */}
                  <Scorecard
                    scorecard={view.scorecard}
                    measures={view.measures}
                    sourceHealth={!isPast ? overview?.sourceHealth : undefined}
                  />
                  <Changes
                    facts={view.facts}
                    previousFacts={previousRunQuery.data?.run.facts ?? null}
                    suppressedKeys={suppressedKeys}
                    onAction={handleAction}
                  />
                </div>
              )}
            </Tabs.Content>

            <Tabs.Content value="runs" className="pt-4 sm:pt-6">
              <div className="space-y-6">
                {/* Source health describes today's collectors, not the
                  historical run being viewed (Karen round 1 F13). */}
                {!isPast && overview && <SourceHealth sourceHealth={overview.sourceHealth} />}
                <RunHistory runs={runs} selectedRunId={runId} onOpenRun={handleOpenRun} />
                <HelmSection title="Past days" lineOn="none">
                  <DayPicker runs={runs} onOpenRun={handleOpenRun} />
                </HelmSection>
              </div>
            </Tabs.Content>

            {!isPast && (
              <Tabs.Content value="settings" className="pt-4 sm:pt-6">
                {configQuery.data && (
                  <SourceConfigEditor
                    config={configQuery.data.config}
                    isDefault={configQuery.data.isDefault}
                    saving={savingConfig}
                    onSave={handleSaveConfig}
                  />
                )}
              </Tabs.Content>
            )}
          </Tabs.Root>
        </div>
      </HelmRecordContext.Provider>
    </HelmReadOnlyContext.Provider>
  );
}
